import React, { useMemo, useState } from "react";
import { Button, Card, CardBody, CardHeader, Chip, Divider, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Switch, Textarea } from "@nextui-org/react";
import { MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import toast from "react-hot-toast";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import ModalAlert from "../../../../components/elements/ModalAlert";
import { deleteFetcher, getFetcher, postFetcher, putFetcher } from "../../../../services/api";
import getErrorMessage from "../../../../services/errorMessage";

const ISIAN_KOSONG = { id: null, pertanyaan: "", jawaban: "", urutan: 0, isActive: true };

/**
 * CMS FAQ - tambah, ubah, hapus pertanyaan yang tampil di halaman publik /faq.
 *
 * Urutan tampil ditentukan kolom "Urutan", bukan waktu pembuatan: pengelola
 * biasanya ingin pertanyaan pembuka tetap di atas meski ditulis belakangan.
 * Sakelar "Aktif" menyembunyikan entri dari halaman publik tanpa menghapusnya.
 */
export default function IndexFaqPage() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [cari, setCari] = useState("");
  const [isian, setIsian] = useState(ISIAN_KOSONG);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [idDihapus, setIdDihapus] = useState(null);
  const [isOpenHapus, setIsOpenHapus] = useState(false);
  const [sedangMengirim, setSedangMengirim] = useState(false);

  const { data, isLoading, mutate } = useSWR("/api/faq/search?returnAll=true", getFetcher);

  const daftar = useMemo(() => {
    const semua = data?.data ?? [];
    const kunci = cari.trim().toLowerCase();
    if (!kunci) return semua;

    return semua.filter((item) => `${item.pertanyaan} ${item.jawaban}`.toLowerCase().includes(kunci));
  }, [data, cari]);

  function bukaTambah() {
    setIsian(ISIAN_KOSONG);
    setIsOpenForm(true);
  }

  function bukaUbah(baris) {
    setIsian({
      id: baris.id,
      pertanyaan: baris.pertanyaan ?? "",
      jawaban: baris.jawaban ?? "",
      urutan: baris.urutan ?? 0,
      isActive: baris.isActive ?? true,
    });
    setIsOpenForm(true);
  }

  async function simpan() {
    if (isian.pertanyaan.trim().length < 5) {
      toast.error("Pertanyaan harus diisi, minimal 5 karakter.");
      return;
    }
    if (isian.jawaban.trim().length < 5) {
      toast.error("Jawaban harus diisi, minimal 5 karakter.");
      return;
    }

    const muatan = {
      pertanyaan: isian.pertanyaan.trim(),
      jawaban: isian.jawaban.trim(),
      urutan: Number(isian.urutan) || 0,
      isActive: isian.isActive,
    };

    try {
      setSedangMengirim(true);
      if (isian.id) {
        await putFetcher("/api/faq", isian.id, muatan);
        toast.success("FAQ berhasil diubah.");
      } else {
        await postFetcher("/api/faq", muatan);
        toast.success("FAQ berhasil ditambah.");
      }
      mutate();
      setIsOpenForm(false);
    } catch (error) {
      toast.error(getErrorMessage(error, "Gagal menyimpan FAQ"));
    } finally {
      setSedangMengirim(false);
    }
  }

  async function hapus() {
    try {
      await deleteFetcher("/api/faq", idDihapus);
      toast.success("FAQ berhasil dihapus.");
      mutate();
    } catch (error) {
      toast.error(getErrorMessage(error, "Gagal menghapus FAQ"));
    } finally {
      setIdDihapus(null);
    }
  }

  const columns = useMemo(
    () => [
      { field: "urutan", headerName: "Urutan", minWidth: 90 },
      { field: "pertanyaan", headerName: "Pertanyaan", minWidth: 300 },
      {
        field: "jawaban",
        headerName: "Jawaban",
        minWidth: 320,
        renderCell: (params) => {
          const teks = params?.row?.jawaban ?? "";
          return teks.length > 120 ? `${teks.slice(0, 120)}...` : teks;
        },
      },
      {
        field: "isActive",
        headerName: "Status",
        minWidth: 110,
        renderCell: (params) => (
          <Chip size="sm" variant="flat" color={params?.row?.isActive ? "success" : "default"}>
            {params?.row?.isActive ? "Aktif" : "Nonaktif"}
          </Chip>
        ),
      },
      {
        field: "action",
        headerName: "Aksi",
        minWidth: 110,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="light" isIconOnly aria-label="Ubah FAQ" title="Ubah FAQ" className="text-ink-700" onPress={() => bukaUbah(params.row)}>
              <PencilSquareIcon className="size-4" />
            </Button>
            <Button
              size="sm"
              variant="light"
              color="danger"
              isIconOnly
              aria-label="Hapus FAQ"
              title="Hapus FAQ"
              onPress={() => {
                setIdDihapus(params.row.id);
                setIsOpenHapus(true);
              }}
            >
              <TrashIcon className="size-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader>
          <p className="text-md">FAQ</p>
        </CardHeader>
        <Divider />
        <CardBody>
          <div className="mb-5 flex flex-col md:flex-row md:items-center gap-3">
            <Button size="sm" color="primary" onPress={bukaTambah} startContent={<PlusIcon className="size-4 stroke-2" />}>
              Tambah
            </Button>
            <Input
              size="sm"
              placeholder="Cari pertanyaan atau jawaban..."
              value={cari}
              onValueChange={setCari}
              isClearable
              onClear={() => setCari("")}
              startContent={<MagnifyingGlassIcon className="size-4" />}
              className="md:max-w-md"
            />
          </div>

          <div className="w-full h-[500px]">
            <ClientDataGrid
              rows={daftar}
              columns={columns}
              rowCount={daftar.length}
              isLoading={isLoading}
              pageSize={pageSize}
              setPageSize={setPageSize}
              page={page}
              setPage={setPage}
              clientPagination
            />
          </div>
        </CardBody>
      </Card>

      <Modal size="2xl" scrollBehavior="inside" isOpen={isOpenForm} onOpenChange={setIsOpenForm} isDismissable={false}>
        <ModalContent>
          <ModalHeader>{isian.id ? "Ubah FAQ" : "Tambah FAQ"}</ModalHeader>
          <ModalBody>
            <Textarea
              label="Pertanyaan"
              labelPlacement="outside"
              placeholder="Tulis pertanyaannya..."
              minRows={2}
              isRequired
              value={isian.pertanyaan}
              onValueChange={(nilai) => setIsian((lama) => ({ ...lama, pertanyaan: nilai }))}
            />
            <Textarea
              label="Jawaban"
              labelPlacement="outside"
              placeholder="Tulis jawabannya..."
              minRows={5}
              isRequired
              value={isian.jawaban}
              onValueChange={(nilai) => setIsian((lama) => ({ ...lama, jawaban: nilai }))}
            />
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <Input
                type="number"
                label="Urutan"
                labelPlacement="outside"
                description="Angka kecil tampil lebih dulu di halaman publik."
                value={String(isian.urutan)}
                onValueChange={(nilai) => setIsian((lama) => ({ ...lama, urutan: nilai }))}
                className="md:max-w-[200px]"
              />
              <Switch size="sm" isSelected={isian.isActive} onValueChange={(nilai) => setIsian((lama) => ({ ...lama, isActive: nilai }))}>
                Tampilkan di halaman publik
              </Switch>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="faded" onPress={() => setIsOpenForm(false)} isDisabled={sedangMengirim}>
              Batal
            </Button>
            <Button size="sm" color="primary" onPress={simpan} isLoading={sedangMengirim}>
              {isian.id ? "Simpan" : "Tambah"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ModalAlert isOpen={isOpenHapus} onOpenChange={setIsOpenHapus} heading="Hapus FAQ ini?" onSubmit={hapus} icon="danger" />
    </RootAdmin>
  );
}
